import React, { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';

interface Image {
  id: string;
  url: string;
  description: string;
}

interface ImageResponse {
  images: string[];
  hasMore: boolean;
  continuationToken?: string;
}

interface FetchImagesProps {
  pageSize: number;
  continuationToken?: string;
}

interface InfiniteImageViewerProps {
  loadImages?: (props: FetchImagesProps) => Promise<ImageResponse>;
  pageSize?: number;
}

// Example image loader function that would be passed as a prop
export const fetchImages: (props: FetchImagesProps) => Promise<ImageResponse> = async ({
  pageSize = 20,
  continuationToken
}) => {
  try {
    // Fetch presigned URLs from API
    const response = await fetch('https://mlf10vrasj.execute-api.us-east-1.amazonaws.com/prod/images', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        pageSize,
        continuationToken,
        optimised: true
      })
    });

    if (!response.ok) throw new Error('Failed to fetch image URLs');
    
    const data = await response.json();
    
    return {
      images: data.images,
      hasMore: data.isTruncated,
      continuationToken: data.continuationToken
    };
  } catch (error) {
    console.error('Error fetching images:', error);
    return { images: [], hasMore: false };
  }
};

const InfiniteImageViewer: React.FC<InfiniteImageViewerProps> = ({ 
  loadImages = fetchImages,
  pageSize = 20 
}) => {
  const [images, setImages] = useState<string[]>([]);
  const [page, setPage] = useState<number>(1);
  const [continuationToken, setContinuationToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const observer = useRef<IntersectionObserver>();
  const lastImageRef = useRef<HTMLDivElement>(null);

  // Reset when category changes
  useEffect(() => {
    setImages([]);
    setPage(1);
    setHasMore(true);
  }, []);

  // Load images when page or category changes
  useEffect(() => {
    const loadMoreImages = async () => {
      try {
        setLoading(true);
        const { images: newImages, hasMore: moreAvailable, continuationToken: nextContinuationToken } = await loadImages({
          pageSize,
          continuationToken
        });
        setImages(prev => page === 1 ? newImages : [...prev, ...newImages]);
        setHasMore(moreAvailable);
        setContinuationToken(nextContinuationToken);
      } catch (error) {
        console.error('Error loading images:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMoreImages();
  }, [page, pageSize, loadImages, continuationToken]);

  // Intersection Observer setup
  useEffect(() => {
    if (!hasMore || loading) return;

    const options: IntersectionObserverInit = {
      root: null,
      rootMargin: '20px',
      threshold: 0.1,
    };

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prev => prev + 1);
      }
    }, options);

    if (lastImageRef.current) {
      observer.current.observe(lastImageRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [hasMore, loading]);

  return (
    <div className="flex h-screen">
      {/* Image Grid */}
      <div className="flex-1 p-4 overflow-y-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {
            images.map((image, index) => {
              const imageDiv = <img
              src={image}
              className="w-full h-full object-cover rounded-lg"
              style={{ paddingLeft: '10px', paddingRight: '10px', paddingBottom: '10px' }}
            />;

            if(index % 4 == 0) {
              return <div className="w-full h-0" style={{
                display: 'flex',
                alignContent: 'space-around',
                flexWrap: 'wrap',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-evenly',
              }}>{imageDiv}</div>;
            } else {
              return imageDiv;
            }
            // <div
            //   key={index}
            //   ref={index === images.length - 1 ? lastImageRef : null}
            //   className="aspect-square relative"
            // >
              
            // </div>
          })}
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div className="flex justify-center p-4">
            <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
          </div>
        )}

        {/* End of Content Message */}
        {!hasMore && !loading && images.length > 0 && (
          <p className="text-center text-gray-500 mt-4">
            No more images to load
          </p>
        )}
      </div>
    </div>
  );
};

export default InfiniteImageViewer;
